// Web: Angemeldet bleiben (localStorage) vs. nur diese Browser-Sitzung (sessionStorage).

export const AUTH_REMEMBER_ME_STORAGE_KEY = "tm_auth_remember_me";

function __hasLocalStorage() {
  try {
    return typeof localStorage !== "undefined";
  } catch {
    return false;
  }
}

function __hasSessionStorage() {
  try {
    return typeof sessionStorage !== "undefined";
  } catch {
    return false;
  }
}

/** @returns {boolean} Standard: dauerhaft angemeldet bleiben. */
export function getAuthRememberMePreference() {
  if (!__hasLocalStorage()) return true;
  try {
    const raw = localStorage.getItem(AUTH_REMEMBER_ME_STORAGE_KEY);
    if (raw === "0" || raw === "false") return false;
    return true;
  } catch {
    return true;
  }
}

/** @param {boolean} remember */
export function setAuthRememberMePreference(remember) {
  if (!__hasLocalStorage()) return;
  try {
    localStorage.setItem(AUTH_REMEMBER_ME_STORAGE_KEY, remember ? "1" : "0");
  } catch (_) {
    // noop
  }
}

function __preferredAuthBackingStore() {
  return getAuthRememberMePreference() && __hasLocalStorage()
    ? localStorage
    : __hasSessionStorage()
      ? sessionStorage
      : __hasLocalStorage()
        ? localStorage
        : null;
}

/** PKCE-OAuth (Google): muss den Redirect über accounts.google.com in localStorage überleben. */
function __isPkceOAuthFlowKey(key) {
  return String(key || "").endsWith("-code-verifier");
}

function __readPkceOAuthFlowItem(key) {
  if (__hasLocalStorage()) {
    try {
      const fromLocal = localStorage.getItem(key);
      if (fromLocal != null) return fromLocal;
    } catch (_) {
      // noop
    }
  }
  if (__hasSessionStorage()) {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }
  return null;
}

function __writePkceOAuthFlowItem(key, value) {
  if (__hasSessionStorage()) {
    try {
      sessionStorage.removeItem(key);
    } catch (_) {
      // noop
    }
  }
  if (__hasLocalStorage()) {
    try {
      localStorage.setItem(key, value);
    } catch (_) {
      // noop
    }
  }
}

/**
 * Supabase-Auth-Storage: schreibt je nach Nutzerwahl in localStorage oder sessionStorage.
 * Liest aus beiden Quellen, damit ein Wechsel der Einstellung die bestehende Session nicht verliert.
 */
export function createAuthPersistenceStorage() {
  return {
    getItem(key) {
      if (__isPkceOAuthFlowKey(key)) {
        return __readPkceOAuthFlowItem(key);
      }
      const preferred = __preferredAuthBackingStore();
      if (!preferred) return null;
      try {
        const fromPreferred = preferred.getItem(key);
        if (fromPreferred != null) return fromPreferred;
      } catch (_) {
        // noop
      }
      const fallback =
        preferred === localStorage && __hasSessionStorage()
          ? sessionStorage
          : preferred === sessionStorage && __hasLocalStorage()
            ? localStorage
            : null;
      if (!fallback) return null;
      try {
        return fallback.getItem(key);
      } catch {
        return null;
      }
    },
    setItem(key, value) {
      if (__isPkceOAuthFlowKey(key)) {
        __writePkceOAuthFlowItem(key, value);
        return;
      }
      const store = __preferredAuthBackingStore();
      if (!store) return;
      try {
        const other =
          store === localStorage && __hasSessionStorage()
            ? sessionStorage
            : store === sessionStorage && __hasLocalStorage()
              ? localStorage
              : null;
        if (other) {
          try {
            other.removeItem(key);
          } catch (_) {
            // noop
          }
        }
        store.setItem(key, value);
      } catch (_) {
        // noop
      }
    },
    removeItem(key) {
      if (__hasLocalStorage()) {
        try {
          localStorage.removeItem(key);
        } catch (_) {
          // noop
        }
      }
      if (__hasSessionStorage()) {
        try {
          sessionStorage.removeItem(key);
        } catch (_) {
          // noop
        }
      }
    },
  };
}
